import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useOutcome } from '../../hooks/useOutcome';
import EditableTitle from '../../components/EditableTitle';
import RemoteReferenceIcon from '../../components/RemoteReferenceIcon';
import DependencyViewer from '../../components/dependencies/DependencyViewer';
import { ScheduleWarningsListEntry } from '../../components/schedule/ScheduleWarningsList';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import WorkMatchPairForm from '../../components/schedule/WorkMatchPairForm';
import WorkMatchList from '../../components/schedule/WorkMatchList';
import TypeBadge from '@/components/common/TypeBadge';
import ImprovementHelper from '@/components/common/ImprovementHelper';
import WatchButton from '@/components/common/WatchButton';
import {
  FiTarget,
  FiTrash2,
  FiPlus,
  FiGitBranch,
  FiUsers,
  FiAlertTriangle,
  FiCheckSquare,
} from 'react-icons/fi';

const OutcomeDetailPage: React.FC = () => {
  const { outcomeId } = useParams<{ outcomeId: string }>();
  const { outcome, loading, error } = useOutcome(outcomeId);
  const { applyCommand, workspaceId } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const [warningText, setWarningText] = useState<string | undefined>(undefined);

  // Deadline form state
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineDescription, setDeadlineDescription] = useState('');

  useEffect(() => {
    if (!safeData || !outcomeId) return;
    let active = true;
    const cb = async (fuzzyResult: any) => {
      if (!active || !fuzzyResult) return;
      try {
        const meta = fuzzyResult.meta;
        const unschedulable = fuzzyResult.schedule?.unschedulable ?? {};
        for (const [nodeId, entity] of Object.entries(meta?.node_to_entity ?? {})) {
          if ((entity as any)?.type === 'Outcome' && (entity as any)?.id === outcomeId) {
            const item = unschedulable[nodeId] as any;
            if (item) {
              setWarningText(item.reason);
              return;
            }
          }
        }
        setWarningText(undefined);
      } catch {
        setWarningText(undefined);
      }
    };
    wasm.DataManager.select_schedule(safeData, cb);
    return () => {
      active = false;
      wasm.DataManager.unsubscribe(safeData, cb);
    };
  }, [safeData, outcomeId]);

  const updateOutcomeTitle = async (newTitle: string) => {
    if (!outcome || !workspaceId) return;
    const command = {
      type: 'UpdateOutcome',
      id: outcome.id,
      actions: [{ type: 'SetTitle', title: newTitle }],
    } as unknown as wasm.DatabaseCommand;
    await applyCommand(command);
  };

  const addDeadline = async () => {
    if (!outcomeId || !deadlineDate) return;
    const command = {
      type: 'UpdateOutcome',
      id: outcomeId,
      actions: [
        {
          type: 'InsertDeadline',
          deadline: {
            date: deadlineDate,
            description: deadlineDescription || '',
          },
        },
      ],
    } as unknown as wasm.DatabaseCommand;
    await applyCommand(command);
    setDeadlineDate('');
    setDeadlineDescription('');
  };

  const deleteDeadline = async (deadlineId: string) => {
    if (!outcomeId) return;
    const command = {
      type: 'UpdateOutcome',
      id: outcomeId,
      actions: [{ type: 'DeleteDeadline', id: deadlineId }],
    } as unknown as wasm.DatabaseCommand;
    await applyCommand(command);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 justify-center p-12 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
        Loading outcome...
      </div>
    );
  }

  if (error || !outcome) {
    return (
      <div className="p-6">
        <div className="p-4 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
          {error || 'Outcome not found'}
        </div>
      </div>
    );
  }

  const formatCompletion = (c: wasm.CompletionCriterion | undefined) => {
    if (!c) return null;
    switch (c.type) {
      case 'DependenciesComplete':
        return 'this outcome is reached when all dependencies are complete';
      case 'Scheduled':
        return 'this outcome is reached when the target date is reached';
      case 'Manual':
        return 'this outcome is reached when it is manually marked complete';
      default:
        return null;
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <EditableTitle
            title={outcome.title}
            onTitleChange={updateOutcomeTitle}
            size="lg"
            className="flex-grow"
          />
          <ImprovementHelper
            entityType="outcome"
            entityName={outcome.title}
            target={outcomeId ? { kind: 'Outcome', id: outcomeId } : undefined}
          />
          {outcomeId && (
            <WatchButton
              workspaceId={workspaceId}
              target={{ type: 'outcome', id: outcomeId }}
              showLabel={false}
            />
          )}
          <RemoteReferenceIcon
            reference={outcome.remoteReference}
            showLabel={true}
          />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <TypeBadge t="Outcome" />
        </div>
      </div>

      {/* Completion criterion */}
      {outcome.completionCriterion && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-2">
            <FiCheckSquare className="w-5 h-5 text-cm-accent-green" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Completion
            </h3>
          </div>
          <p className="text-sm text-cm-text-secondary">
            {formatCompletion(outcome.completionCriterion)}
          </p>
        </div>
      )}

      {/* Deadlines */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiTarget className="w-5 h-5 text-cm-accent-orange" />
          <h3 className="font-mono font-medium text-cm-text-primary">
            Deadlines
          </h3>
        </div>

        {outcome.deadlines && outcome.deadlines.length > 0 ? (
          <div className="divide-y divide-cm-border-subtle mb-4">
            {outcome.deadlines
              .slice()
              .sort((a, b) => a.date.localeCompare(b.date))
              .map((deadline, index) => {
                const isPast = deadline.date < today;
                return (
                  <div
                    key={deadline.id || index}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`text-sm font-mono ${isPast ? 'text-red-400' : 'text-cm-text-secondary'}`}
                      >
                        {new Date(
                          deadline.date + 'T12:00:00',
                        ).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                      {deadline.description && (
                        <span className="text-sm text-cm-text-primary truncate">
                          {deadline.description}
                        </span>
                      )}
                      {isPast && (
                        <span className="text-xs text-red-400 font-medium">
                          overdue
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => deleteDeadline(deadline.id)}
                      className="text-cm-text-muted hover:text-red-400 p-1 rounded-full hover:bg-cm-bg-hover transition-colors shrink-0"
                      aria-label="Delete deadline"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
          </div>
        ) : (
          <p className="text-sm text-cm-text-muted text-center py-4 mb-4">
            No deadlines set.
          </p>
        )}

        {/* Add deadline form */}
        <div className="flex items-end gap-3 pt-3 border-t border-cm-border-subtle">
          <div className="flex-shrink-0">
            <label className="block text-xs text-cm-text-muted mb-1">
              Date
            </label>
            <input
              type="date"
              value={deadlineDate}
              onChange={(e) => setDeadlineDate(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-cm-border-subtle bg-cm-bg-deep text-sm text-cm-text-primary focus:outline-none focus:border-cm-accent-orange"
            />
          </div>
          <div className="flex-grow">
            <label className="block text-xs text-cm-text-muted mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={deadlineDescription}
              onChange={(e) => setDeadlineDescription(e.target.value)}
              placeholder="e.g. Beta release, Sprint end"
              className="w-full px-3 py-1.5 rounded-lg border border-cm-border-subtle bg-cm-bg-deep text-sm text-cm-text-primary placeholder:text-cm-text-muted focus:outline-none focus:border-cm-accent-orange"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deadlineDate) addDeadline();
              }}
            />
          </div>
          <button
            onClick={addDeadline}
            disabled={!deadlineDate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cm-accent-orange text-cm-bg-deep text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            <FiPlus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Dependencies & Schedule */}
      {outcomeId && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiGitBranch className="w-5 h-5 text-cm-accent-purple" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Dependencies & Schedule
            </h3>
          </div>
          <DependencyViewer nodeId={{ Outcome: outcomeId }} height="500px" />
        </div>
      )}

      {/* Work Matching */}
      {outcomeId && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiUsers className="w-5 h-5 text-cm-accent-green" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Work Matching
            </h3>
          </div>
          <p className="text-sm text-cm-text-muted mb-3">
            Set work matches to enable automatic scheduling.
          </p>
          <WorkMatchList
            type="dependency"
            dependency={{ kind: 'Outcome', id: outcomeId }}
          />
          <WorkMatchPairForm
            context={{
              kind: 'Outcome',
              id: outcomeId,
              projectId: null,
            }}
          />
        </div>
      )}

      {/* Scheduling warnings */}
      {outcomeId && warningText && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiAlertTriangle className="w-5 h-5 text-cm-accent-orange" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Scheduling
            </h3>
          </div>
          <ScheduleWarningsListEntry
            taskOrOutcome={{ type: 'outcome', id: outcomeId }}
          />
        </div>
      )}
    </div>
  );
};

export default OutcomeDetailPage;
